import { createContext, useState, useContext, useMemo } from 'react';

import type { GetSellerWhitelabellingResponse } from '@iwoca/lapi-client/iwocapay';

export type WhiteLabelTheme = GetSellerWhitelabellingResponse['data'];

type WhiteLabelProviderValue = {
  theme: WhiteLabelTheme;
  setTheme: React.Dispatch<React.SetStateAction<WhiteLabelTheme>>;
  // derived values
  showSellerLogo: boolean;
  isTheme: boolean;
  isThemeResolved: boolean;
  setIsThemeResolved: React.Dispatch<React.SetStateAction<boolean>>;
};

const INITIAL_THEME: WhiteLabelTheme = {
  logo_uri: null,
  preferred_logo_type: null,
  primary_color: null,
  secondary_color: null,
  text_color: null,
};

const WhiteLabelContext = createContext<WhiteLabelProviderValue | undefined>(
  undefined,
);

export const WhiteLabelProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [theme, setTheme] = useState<WhiteLabelTheme>(INITIAL_THEME);
  const [isThemeResolved, setIsThemeResolved] = useState(false);

  const showSellerLogo = useMemo(
    () => !!theme.logo_uri && !!theme.preferred_logo_type,
    [theme.logo_uri, theme.preferred_logo_type],
  );

  const isTheme = useMemo(
    () =>
      !!theme.primary_color || !!theme.secondary_color || !!theme.text_color,
    [theme.primary_color, theme.secondary_color, theme.text_color],
  );

  return (
    <WhiteLabelContext.Provider
      value={{
        theme,
        setTheme,
        showSellerLogo,
        isTheme,
        isThemeResolved,
        setIsThemeResolved,
      }}
    >
      {children}
    </WhiteLabelContext.Provider>
  );
};

export function useWhiteLabelTheme() {
  const ctx = useContext(WhiteLabelContext);
  if (!ctx)
    throw new Error(
      'useWhiteLabelTheme must be used within a WhiteLabelProvider',
    );
  return ctx;
}
